/**
 * PROGLADS — Search overlay enhancements (Concept A).
 *
 * Adds three things on top of Horizon's native predictive search, without
 * forking assets/predictive-search.js:
 *
 *   1. Recent searches, persisted in localStorage and rendered into the
 *      empty state.
 *   2. `/` and Ctrl+K as open shortcuts (the theme already handles Cmd+K).
 *   3. A live result count in the panel footer.
 *
 * Native predictive-search.js replaces the results container wholesale via
 * morph() on every keystroke, so anything we inject has to be re-hydrated
 * afterwards. A MutationObserver on the results container handles that.
 */

const STORAGE_KEY = 'pg:recent-searches';
const MAX_RECENTS = 6;
const MODAL_ID = 'search-modal';

/* ---------------------------------------------------------------- storage */

/** @returns {string[]} */
function readRecents() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((term) => typeof term === 'string' && term.trim()).slice(0, MAX_RECENTS);
  } catch {
    // Private browsing, quota, or corrupt payload — recents are optional.
    return [];
  }
}

/** @param {string[]} terms */
function writeRecents(terms) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(terms.slice(0, MAX_RECENTS)));
  } catch {
    /* no-op */
  }
}

/** @param {string} term */
function addRecent(term) {
  const clean = String(term || '')
    .trim()
    .replace(/\s+/g, ' ');

  // Ignore the id:… queries the theme uses internally for recently-viewed.
  if (!clean || clean.length > 64 || clean.startsWith('id:')) return;

  const lower = clean.toLowerCase();
  const next = [clean, ...readRecents().filter((t) => t.toLowerCase() !== lower)];
  writeRecents(next);
}

/** @param {string} term */
function removeRecent(term) {
  const lower = term.toLowerCase();
  writeRecents(readRecents().filter((t) => t.toLowerCase() !== lower));
}

/* ------------------------------------------------------------------- dom */

const CLOCK_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 1.8"/></svg>';

const CLOSE_ICON =
  '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>';

/** @param {string} value */
function escapeHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
  );
}

/**
 * Fill a `[data-pg-recents]` container with the stored terms.
 * @param {HTMLElement} host
 */
function renderRecents(host) {
  const terms = readRecents();

  if (!terms.length) {
    host.hidden = true;
    host.innerHTML = '';
    delete host.dataset.pgHydrated;
    return;
  }

  const signature = terms.join('\u0000');
  if (host.dataset.pgHydrated === signature) return;

  const labels = {
    heading: host.dataset.labelHeading || 'Recent searches',
    clear: host.dataset.labelClear || 'Clear all',
    remove: host.dataset.labelRemove || 'Remove',
  };

  host.hidden = false;
  host.innerHTML = `
    <div class="pg-recents__head">
      <h4>${escapeHtml(labels.heading)}</h4>
      <button type="button" class="pg-recents__clear" data-pg-recents-clear>${escapeHtml(labels.clear)}</button>
    </div>
    <ul class="pg-recents__list">
      ${terms
        .map(
          (term) => `
        <li class="pg-recents__item">
          <button type="button" class="pg-recents__row" data-pg-recent="${escapeHtml(term)}">
            ${CLOCK_ICON}
            <span class="pg-recents__term">${escapeHtml(term)}</span>
          </button>
          <button type="button" class="pg-recents__remove"
                  data-pg-recent-remove="${escapeHtml(term)}"
                  aria-label="${escapeHtml(`${labels.remove} ${term}`)}">${CLOSE_ICON}</button>
        </li>`
        )
        .join('')}
    </ul>`;

  host.dataset.pgHydrated = signature;
}

/**
 * Mirror the result count reported by the section into the footer.
 * @param {HTMLElement} root
 */
function renderCount(root) {
  const slot = root.querySelector('[data-pg-count]');
  if (!(slot instanceof HTMLElement)) return;

  const results = root.querySelector('[data-search-results]');
  const count = results instanceof HTMLElement ? Number(results.dataset.resultsCount || 0) : 0;
  const terms = results instanceof HTMLElement ? results.dataset.resultsTerms || '' : '';

  if (!count || !terms) {
    slot.textContent = '';
    return;
  }

  const template = count === 1 ? slot.dataset.templateOne : slot.dataset.templateMany;
  slot.textContent = (template || '').replace('%count%', String(count)).replace('%terms%', terms);
}

/* ------------------------------------------------------------------ wiring */

/** @param {HTMLElement} root - the <predictive-search-component> element */
function init(root) {
  if (root.dataset.pgSearchReady === 'true') return;
  root.dataset.pgSearchReady = 'true';

  const input = /** @type {HTMLInputElement | null} */ (root.querySelector('.search-input'));
  const form = root.querySelector('form');

  const hydrate = () => {
    root.querySelectorAll('[data-pg-recents]').forEach((host) => {
      if (host instanceof HTMLElement) renderRecents(host);
    });
    renderCount(root);
  };

  hydrate();

  // predictive-search.js morphs the results container on every keystroke,
  // which wipes anything we injected. Re-hydrate after each mutation.
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      hydrate();
    });
  });

  const contentEl = root.querySelector('.predictive-search-form__content');
  if (contentEl) observer.observe(contentEl, { childList: true, subtree: true });

  /* --- record terms ------------------------------------------------------ */

  const record = () => {
    if (input) addRecent(input.value);
  };

  form?.addEventListener('submit', record);

  // Enter with no result highlighted navigates directly rather than
  // submitting, so capture it on the input as well.
  input?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') record();
  });

  root.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[ref="viewAllButton"], .predictive-search-results__card a')) {
      record();
      return;
    }

    const remove = target.closest('[data-pg-recent-remove]');
    if (remove instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      removeRecent(remove.dataset.pgRecentRemove || '');
      hydrate();
      input?.focus();
      return;
    }

    if (target.closest('[data-pg-recents-clear]')) {
      event.preventDefault();
      writeRecents([]);
      hydrate();
      input?.focus();
      return;
    }

    const row = target.closest('[data-pg-recent]');
    if (row instanceof HTMLElement && input) {
      event.preventDefault();
      input.value = row.dataset.pgRecent || '';
      // inputType is required — predictive-search.js ignores synthetic
      // input events without one.
      input.dispatchEvent(new InputEvent('input', { inputType: 'insertText', bubbles: true }));
      input.focus();
    }
  });
}

/* ---------------------------------------------------------------- shortcuts */

function bindShortcuts() {
  document.addEventListener('keydown', (event) => {
    // Cmd+K is already handled by predictive-search.js; only add Ctrl+K.
    const isCtrlK = event.ctrlKey && !event.metaKey && event.key.toLowerCase() === 'k';
    const isSlash = event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (!isCtrlK && !isSlash) return;

    const active = document.activeElement;
    const isTyping =
      active instanceof HTMLElement &&
      (active.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName));
    if (isTyping) return;

    const modal = document.getElementById(MODAL_ID);
    const dialog = modal?.querySelector('dialog');
    if (!modal || !dialog || dialog.hasAttribute('open')) return;

    event.preventDefault();
    /** @type {any} */ (modal).showDialog?.();
  });
}

/* ------------------------------------------------------------------- boot */

function boot() {
  document.querySelectorAll('predictive-search-component').forEach((node) => {
    if (node instanceof HTMLElement && node.closest(`#${MODAL_ID}`)) init(node);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

bindShortcuts();
